import { inject } from "fw";
import { createFrom, createFromArray } from "fw-model";

import { Manager } from "./manager";
import { AtsOrganizationTemplate } from "models/ats-organization-template";

export type AtsOrganizationTemplatePostArgs = {
    Name: string;
    AtsOrganizationId: string;
    AtsOrganizationSeasonId: string;
    Paths: string[];
};

export type AtsOrganizationTemplatePutArgs = {
    Name: string;
    Paths: string[];
};

export type ManualOrganizationTemplateArgs = {
    Name: string;
    SeasonConfiguration: any;
};

@inject
export class AtsTemplateRepository {
    constructor(private s: Manager) {}

    async get(id: string, environment: string = null) {
        const res = await this.s.get(`ats-org-template/${id}`, { environment });
        return createFrom(AtsOrganizationTemplate, res.body);
    }

    async listByIds(ids: string[]) {
        const res = await this.s.post<any[]>("ats-org-template/list-by-ids", ids);
        return createFromArray(AtsOrganizationTemplate, res.body);
    }

    async list(
        f: string = null,
        s: string = null,
        page = 1,
        pageSize = 20,
        sort: string = null,
        fields: string = null,
        excludeMappedTemplatesForOrg: string = null,
        environment: string = null
    ) {
        const res = await this.s.get<any[]>("ats-org-template", { page, pageSize, f, s, sort, fields, excludeMappedTemplatesForOrg, environment });
        return {
            total: parseInt(res.headers["x-manager-total"], 10),
            result: createFromArray(AtsOrganizationTemplate, res.body)
        };
    }

    async post(obj: AtsOrganizationTemplatePostArgs) {
        const res = await this.s.post("ats-org-template", obj);
        return createFrom(AtsOrganizationTemplate, res.body);
    }

    async postManual(args: ManualOrganizationTemplateArgs) {
        const res = await this.s.post("ats-org-template/manual", args);
        return createFrom(AtsOrganizationTemplate, res.body);
    }

    async put(id: string, args: AtsOrganizationTemplatePutArgs) {
        const res = await this.s.put(`ats-org-template/${id}`, args);
        return createFrom(AtsOrganizationTemplate, res.body);
    }

    async putManual(id: string, args: ManualOrganizationTemplateArgs) {
        const res = await this.s.put(`ats-org-template/${id}/manual`, args);
        return createFrom(AtsOrganizationTemplate, res.body);
    }

    async remove(id: string) {
        await this.s.delete(`ats-org-template/${id}`);
    }

    async hasConfigurationUpdates(id: string) {
        const res = await this.s.get<any>(`ats-org-template/${id}/has-configuration-updates`);
        return res.body;
    }

    async updateConfiguration(id: string, paths: any[]) {
        const res = await this.s.post(`ats-org-template/${id}/update-configuration`, { paths });
        return createFrom(AtsOrganizationTemplate, res.body);
    }

    async getConfigurationBySeasonId(seasonId: string) {
        const res = await this.s.get<any>(`ats-org-template/season-configuration/${seasonId}`);

        if (res.headers["content-type"].includes("application/json")) {
            return res.body;
        }

        throw "Incorrect content type";
    }

    async environments(): Promise<string[]> {
        const res = await this.s.get<string[]>(`ats-org-template/environments`);
        return res.body;
    }
}
